import { getReportReasonLabel, REPORT_REASONS, type ReportReason, reportArticle } from "@serophin/nottldr-shared";
import { useState } from "preact/hooks";
import { useToast } from "../../contexts/ToastContext";
import { Modal } from "../Modal";

export type ReportArticleModalProps = {
  isOpen: boolean;
  onClose: () => void;
  articleId: string;
  authorAlias: string;
};

const FIELD_CLASS =
  "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500";
const LABEL_CLASS = "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2";

const useReport = (authorAlias: string, articleId: string, onClose: () => void) => {
  const toast = useToast();
  const [reason, setReason] = useState<ReportReason | "">("");
  const [explanation, setExplanation] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const needsExplanation = reason === "other";
  const isValid = reason !== "" && (!needsExplanation || explanation.trim() !== "");

  const close = () => {
    setReason("");
    setExplanation("");
    onClose();
  };

  const submit = async () => {
    if (reason === "" || !isValid) {
      return;
    }
    setIsSubmitting(true);
    try {
      await reportArticle(authorAlias, articleId, reason, explanation || undefined);
      toast.success("Report submitted successfully. Thank you for your feedback!");
      close();
    } catch (error) {
      const tooMany = (error as { status?: number } | undefined)?.status === 429;
      toast.error(tooMany ? "Too many reports. Please try again later." : "Failed to submit report. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return { reason, setReason, explanation, setExplanation, isSubmitting, needsExplanation, isValid, close, submit };
};

export const ReportArticleModal = ({ isOpen, onClose, articleId, authorAlias }: ReportArticleModalProps) => {
  const report = useReport(authorAlias, articleId, onClose);

  return (
    <Modal isOpen={isOpen} onClose={report.close}>
      <div class="py-2">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Report Article</h3>
        <div class="space-y-4">
          <div>
            <label class={LABEL_CLASS}>Reason for reporting *</label>
            <select value={report.reason} onChange={(event) => report.setReason(event.currentTarget.value as ReportReason | "")} class={FIELD_CLASS}>
              <option value="">Select a reason</option>
              {REPORT_REASONS.map((reason) => (
                <option key={reason} value={reason}>
                  {getReportReasonLabel(reason)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label class={LABEL_CLASS}>Additional explanation {report.needsExplanation ? "*" : "(optional)"}</label>
            <textarea
              value={report.explanation}
              onInput={(event) => report.setExplanation(event.currentTarget.value)}
              rows={4}
              maxLength={250}
              class={`${FIELD_CLASS} resize-none`}
              placeholder={report.needsExplanation ? "Please explain your reason for reporting..." : "Please provide more details..."}
            />
          </div>
          <div class="flex space-x-3 pt-2">
            <button
              type="button"
              onClick={report.close}
              disabled={report.isSubmitting}
              class="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={report.submit}
              disabled={!report.isValid || report.isSubmitting}
              class="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {report.isSubmitting ? "Submitting..." : "Submit"}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
